import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  ListItemButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { DndContext, DragOverlay, useDraggable, useDroppable } from "@dnd-kit/core";
import { addWeeks, isToday, isWeekend } from "date-fns";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { CmmsEmptyState, CmmsErrorState, CmmsListSkeleton } from "../../cmms/components/CmmsStates";
import {
  cmmsTypeScale,
  getAssetHierarchyPalette,
  getAssetHierarchyPanelSx,
  getIconButtonSx,
  getOutlinedButtonSx,
  getPanelTitleSx,
  getToggleButtonGroupSx,
} from "../../cmms/assetHierarchyStyles";
import { getTypeBadge } from "../workOrderEnums";
import {
  WeekGroupBy,
  getDayColumns,
  getEstimateTotal,
  getMoveTargets,
  getProjectedRows,
  getWeekInfo,
  getWeekRows,
  getWeekStart,
  groupWeekRows,
} from "../workOrderWeek";
import { useProjectedOccurrences } from "../useProjectedOccurrences";
import { useWorkOrderMove } from "../useWorkOrderMove";
import {
  DAY_OPTIONS,
  DayHours,
  EstimateSummary,
  LONG_DAY_OPTIONS,
  MOVE_HOST_SX,
  MoveMenu,
  MoveUndoSnackbar,
  WeekCardContent,
  getCalendarShades,
  getCardSx,
  getDropTargetSx,
  isDropTarget,
  useDragToMove,
} from "./workOrderCalendarParts";
import messages from "../messages";

const groupByLocalStorageName = "cmmsWorkOrders-weekGroupBy";

// Narrow enough for seven days on a laptop, wide enough for a card's badges side by side.
const DAY_MIN_WIDTH = 132;

const keepAll = (occurrences) => occurrences;

/*
 * A column's shade. Today before the weekend, since a Saturday shift is still today. The late
 * column has its own, so the work in it does not read as Monday's.
 */
const getColumnBg = (date, shades) => {
  if (date === null) return shades.late;
  if (isToday(date)) return shades.today;
  if (isWeekend(date)) return shades.weekend;
  return undefined;
};

const getCellSx = (date, index, shades, palette) => ({
  backgroundColor: getColumnBg(date, shades),
  borderLeft: index > 0 ? `1px solid ${palette.border}` : 0,
  minWidth: 0,
});

/*
 * One order in a day.
 *
 * A projected card does not open the drawer, because there is no order to open, and its border is
 * dashed. It is drawn as a plain box rather than a disabled button, which a screen reader would
 * announce as a control that does not work.
 *
 * A manager can move preventive work or a problem to another day of the week, the days
 * getMoveTargets gives, by dragging it there or from its menu (MoveMenu). onMove is only passed to
 * a manager. A card with nowhere to go can neither be dragged nor has the button, so a past week
 * has neither. groupKey is the card's panel, since a card is only dropped on a day in its own.
 */
function WeekCard({ row, acute, groupKey, onClick, weekStart, onMove, movingId, palette, ...contentProps }) {
  const intl = useIntl();
  const { workOrder, projected } = row;
  const type = getTypeBadge(workOrder.workOrderType, intl);
  const moveTargets = onMove ? getMoveTargets(row, weekStart) : [];
  const canMove = moveTargets.length > 0;
  // Not while a move is still on its way, since handleMove would drop the second one unsaid.
  const { setNodeRef, listeners, isDragging } = useDraggable({
    id: row.key,
    data: { row, acute, groupKey },
    disabled: !canMove || movingId !== null,
  });

  const cardSx = getCardSx(projected, type.bg, palette);
  const content = (
    <WeekCardContent row={row} acute={acute} hasMoveButton={canMove} palette={palette} {...contentProps} />
  );

  if (projected) {
    return <Box sx={cardSx}>{content}</Box>;
  }

  /*
   * The border lights up on hover rather than the background, which would let the column's shade
   * show through the card. The type's edge keeps its colour.
   *
   * Faded while it is dragged, since the copy under the pointer is the one to look at, and while a
   * move is on its way to the server. On a touch screen a press held on a card that moves starts a
   * drag, so the browser is not to zoom on it or open its own menu for it.
   */
  const card = (
    <ListItemButton
      ref={setNodeRef}
      {...listeners}
      data-work-order-id={workOrder.id}
      onClick={() => onClick?.(workOrder)}
      sx={{
        ...cardSx,
        opacity: isDragging || movingId === workOrder.id ? 0.5 : 1,
        ...(canMove ? { touchAction: "manipulation", WebkitTouchCallout: "none" } : {}),
        "&:hover": { backgroundColor: palette.surface, borderColor: palette.selectedBorder, borderLeftColor: type.bg },
      }}
    >
      {content}
    </ListItemButton>
  );

  if (!canMove) return card;

  return (
    <Box sx={MOVE_HOST_SX}>
      {card}
      <MoveMenu row={row} targets={moveTargets} onMove={onMove} palette={palette} />
    </Box>
  );
}

/*
 * The card under the pointer while it is dragged. A copy, since the card itself stays where it
 * was, faded, until it is dropped. It floats over the page, so it has the shadow menus have.
 */
function DraggedCard({ row, acute, palette, ...contentProps }) {
  const intl = useIntl();
  const type = getTypeBadge(row.workOrder.workOrderType, intl);
  return (
    <Box sx={{ ...getCardSx(false, type.bg, palette), boxShadow: palette.shadow, cursor: "grabbing" }}>
      <WeekCardContent row={row} acute={acute} hasMoveButton palette={palette} {...contentProps} />
    </Box>
  );
}

/*
 * A day's name, then what the group has that day: how many orders and the hours they are
 * estimated at, akut and planned together. The hours follow the week's rule (see showEstimates
 * below), marked as DayHours marks them.
 */
function DayHeader({ column, index, showEstimates, shades, palette }) {
  const intl = useIntl();
  const { date, count, estimatedHours, withoutEstimateCount } = column;
  const isLate = date === null;
  const today = !isLate && isToday(date);

  return (
    <Box
      aria-current={today ? "date" : undefined}
      sx={{ ...getCellSx(date, index, shades, palette), borderBottom: `1px solid ${palette.border}`, px: 1, py: 0.75 }}
    >
      <Typography
        variant="body2"
        sx={{
          color: today ? palette.accent : isLate ? shades.lateText : palette.text,
          fontWeight: today || isLate ? 700 : 500,
        }}
      >
        {isLate
          ? (
            <Tooltip title={intl.formatMessage(messages.weekOverdueTooltip)}>
              <Box component="span" sx={{ cursor: "help" }}>{intl.formatMessage(messages.weekOverdue)}</Box>
            </Tooltip>
          )
          : intl.formatDate(date, DAY_OPTIONS)}
      </Typography>
      {count > 0 &&
        <Typography variant="caption" sx={{ color: palette.mutedText, display: "block" }}>
          {intl.formatMessage(messages.weekDayCount, { count })}
          {showEstimates &&
            <>
              {" · "}
              <Box component="span" sx={{ color: palette.text, fontWeight: 700 }}>
                <DayHours estimatedHours={estimatedHours} withoutEstimateCount={withoutEstimateCount} />
              </Box>
            </>
          }
        </Typography>
      }
    </Box>
  );
}

/*
 * One day's cards, akut first: what came in needs someone before what was scheduled does.
 * workOrderWeek.js has already sorted each kind by when.
 *
 * While a card is dragged, a day it can be dropped on is outlined, and the one under the pointer
 * is filled in as well. Any other day, the late column among them, takes no drop and stays as it
 * is.
 */
function DayCell({ column, index, groupKey, dropTarget, shades, palette, cardProps }) {
  const intl = useIntl();
  const { setNodeRef, isOver } = useDroppable({
    id: `${groupKey}:${column.date?.getTime() ?? "late"}`,
    data: { date: column.date },
    disabled: !dropTarget,
  });
  const hasCards = column.count > 0;
  // Named for a screen reader, which reads the cards without the header above them.
  const dayName = column.date === null
    ? intl.formatMessage(messages.weekOverdue)
    : intl.formatDate(column.date, LONG_DAY_OPTIONS);

  return (
    <Box
      ref={setNodeRef}
      role={hasCards ? "group" : undefined}
      aria-label={hasCards ? dayName : undefined}
      sx={{
        ...getCellSx(column.date, index, shades, palette),
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        p: 0.75,
        ...(dropTarget ? getDropTargetSx(isOver, palette) : {}),
      }}
    >
      {column.acute.map((row) => (
        <WeekCard key={row.key} row={row} acute groupKey={groupKey} palette={palette} {...cardProps} />
      ))}
      {column.planned.map((row) => (
        <WeekCard key={row.key} row={row} acute={false} groupKey={groupKey} palette={palette} {...cardProps} />
      ))}
    </Box>
  );
}

/*
 * One group's week as days: a header per day, then a cell per day with its cards.
 *
 * A column is never narrower than a card can be read at. Where the screen has no room for all of
 * them, the grid scrolls sideways inside the panel rather than squeezing the cards.
 */
function WeekGrid({ columns, groupKey, drag, showEstimates, shades, palette, cardProps }) {
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${columns.length}, minmax(${DAY_MIN_WIDTH}px, 1fr))`,
          // So the grid itself is as wide as its columns, and its shades and borders reach the end
          // of the scroll.
          minWidth: columns.length * DAY_MIN_WIDTH,
        }}
      >
        {columns.map((column, index) => (
          <DayHeader
            key={column.date?.getTime() ?? "late"}
            column={column}
            index={index}
            showEstimates={showEstimates}
            shades={shades}
            palette={palette}
          />
        ))}
        {columns.map((column, index) => (
          <DayCell
            key={column.date?.getTime() ?? "late"}
            column={column}
            index={index}
            groupKey={groupKey}
            dropTarget={isDropTarget(drag, column.date, groupKey)}
            shades={shades}
            palette={palette}
            cardProps={cardProps}
          />
        ))}
      </Box>
    </Box>
  );
}

/*
 * The work-orders page as weeks instead of one long list: per department or per trade, what came
 * in (akut) and what was scheduled (planerad) in the week being looked at, side by side on the day
 * it falls on. Work still open from earlier weeks gets a column of its own before Monday, in every
 * group, so the days line up from one group to the next.
 *
 * Reads the list the page has already filtered, so the dropdowns, the search and Mina mean the
 * same here as above the grid. Which week an order lands in, and what counts as akut or as late,
 * is workOrderWeek.js's to decide; this only draws it.
 *
 * From the current week on, it also shows the orders the calendar schedules are still to raise,
 * so next week's preventive work is there before the sweep creates it. Those come from the
 * schedules, not from the page's list, so the page passes filterProjected to apply its filters to
 * them as well.
 *
 * A manager can move preventive work or a problem to another day of the week on screen, by
 * dragging the card or from its menu, for the day akut work took over (useWorkOrderMove).
 * workOrderWeek.js says what moves and where to.
 *
 * A card tags its trade where tradeTagById is passed, which the page does only while the trade
 * filter is on every trade. Not while the week is grouped by trade, when the panel already says it.
 *
 * Always opens on the current week. The week is where you are looking, not a preference, while
 * the grouping is how you work and is remembered.
 */
export default function WorkOrderWeekView({
  companyKey,
  workOrders,
  isLoading,
  assetNodeNameById,
  userNameById,
  tradeOptions,
  tradeTagById,
  filterProjected = keepAll,
  onRowClick,
}) {
  const intl = useIntl();
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);

  const [weekStart, setWeekStart] = useState(() => getWeekStart());
  const [storedGroupBy, setGroupBy] = useLocalStorage(groupByLocalStorageName, WeekGroupBy.Department);

  const currentWeekStart = getWeekStart();
  const isCurrentWeek = weekStart.getTime() === currentWeekStart.getTime();

  // What the schedules are still to raise in this week. A past week is not asked for: a schedule
  // that still owes a run from then is overdue, and the current week shows that run.
  const showsProjection = Boolean(companyKey) && weekStart >= currentWeekStart;
  const projectedQuery = useProjectedOccurrences(companyKey, weekStart, addWeeks(weekStart, 1), showsProjection);
  const occurrences = showsProjection ? projectedQuery.data : null;

  // Both lists before anything is drawn, so a week's sums never change in front of the reader as
  // the second list arrives, and a future week does not flash "nothing here" first.
  const isWeekLoading = isLoading || (showsProjection && projectedQuery.isLoading);

  /*
   * Trade grouping only once there are trades, the same rule as the page's trade filter. A
   * remembered choice for trade falls back to department rather than putting the whole week in
   * "No trade" for a company that has none.
   */
  const hasTrades = (tradeOptions?.length ?? 0) > 0;
  const groupBy = hasTrades && storedGroupBy === WeekGroupBy.Trade ? WeekGroupBy.Trade : WeekGroupBy.Department;

  // The filter's options, so a retired trade is named here the way the dropdown names it.
  const tradeNameById = useMemo(
    () => new Map((tradeOptions ?? []).map((option) => [option.id, option.title])),
    [tradeOptions],
  );

  const { groups, weekEstimate, hasLate } = useMemo(() => {
    // Departments are asset nodes, named from the same tree the asset column is.
    const getLabel = groupBy === WeekGroupBy.Trade
      ? (id) => tradeNameById.get(id)
      : (id) => assetNodeNameById?.get(id);
    const rows = [
      ...getWeekRows(workOrders, weekStart),
      ...getProjectedRows(filterProjected(occurrences ?? []), weekStart),
    ];
    return {
      groups: groupWeekRows(rows, groupBy, getLabel).map((group) => ({ ...group, columns: getDayColumns(group, weekStart) })),
      weekEstimate: getEstimateTotal(rows),
      // For the week, not per group: a group with nothing late still gets the column, empty.
      hasLate: rows.some((row) => row.carriedOver),
    };
  }, [workOrders, occurrences, filterProjected, weekStart, groupBy, tradeNameById, assetNodeNameById]);

  // Moving a card to another day, from its menu or by dragging it there.
  const weekRef = useRef(null);
  const { canMove, movingId, handleMove, notice, closeNotice, handleUndo } = useWorkOrderMove({
    companyKey,
    containerRef: weekRef,
    periodKey: weekStart.getTime(),
    layout: groups,
  });
  const { drag, dndProps } = useDragToMove({
    getTargets: (row) => getMoveTargets(row, weekStart),
    onMove: handleMove,
  });

  const totals = groups.reduce(
    (sum, group) => ({ acute: sum.acute + group.acute.length, planned: sum.planned + group.planned.length }),
    { acute: 0, planned: 0 },
  );

  /*
   * Hours only for a week where someone has estimated something. A company that does not use
   * estimates would otherwise read "5 without estimate" on every group, every week, which says
   * nothing; once one order in the week has an estimate, the unestimated rest is worth pointing out.
   */
  const showEstimates = weekEstimate.estimatedHours !== null;

  const week = getWeekInfo(weekStart);

  // The year only when it is not this one, which is also what tells week 1 of next year from
  // week 1 of this.
  const thisYear = new Date().getFullYear();
  const range = intl.formatDateTimeRange(week.start, week.end, {
    day: "numeric",
    month: "short",
    ...(week.start.getFullYear() !== thisYear || week.end.getFullYear() !== thisYear ? { year: "numeric" } : {}),
  });

  const noGroupLabel = intl.formatMessage(groupBy === WeekGroupBy.Trade ? messages.weekNoTrade : messages.weekNoDepartment);
  const cardTradeTags = groupBy === WeekGroupBy.Trade ? null : tradeTagById;
  const cardProps = {
    assetNodeNameById,
    userNameById,
    tradeTagById: cardTradeTags,
    onClick: onRowClick,
    weekStart,
    onMove: canMove ? handleMove : null,
    movingId,
  };

  const shades = getCalendarShades(theme, cmmsColors);

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          <Tooltip title={intl.formatMessage(messages.weekPrevious)}>
            <IconButton
              aria-label={intl.formatMessage(messages.weekPrevious)}
              onClick={() => setWeekStart((current) => addWeeks(current, -1))}
              sx={getIconButtonSx(cmmsColors)}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          {/* Fixed width, so stepping from a short week label to a long one does not move the arrows. */}
          <Typography
            variant={cmmsTypeScale.statValue}
            sx={{ ...getPanelTitleSx(cmmsColors), minWidth: { sm: 260 }, textAlign: "center" }}
          >
            {intl.formatMessage(messages.weekLabel, { weekNumber: week.weekNumber, range })}
          </Typography>
          <Tooltip title={intl.formatMessage(messages.weekNext)}>
            <IconButton
              aria-label={intl.formatMessage(messages.weekNext)}
              onClick={() => setWeekStart((current) => addWeeks(current, 1))}
              sx={getIconButtonSx(cmmsColors)}
            >
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            onClick={() => setWeekStart(currentWeekStart)}
            disabled={isCurrentWeek}
            sx={getOutlinedButtonSx(cmmsColors)}
          >
            <FormattedMessage {...messages.weekCurrent} />
          </Button>
          <Typography variant="body2" sx={{ color: cmmsColors.mutedText, ml: 1 }}>
            {intl.formatMessage(messages.weekCounts, totals)}
          </Typography>
          {showEstimates &&
            <EstimateSummary {...weekEstimate} palette={cmmsColors} />
          }
        </Box>

        {hasTrades &&
          <ToggleButtonGroup
            exclusive
            size="small"
            value={groupBy}
            onChange={(_event, value) => { if (value != null) setGroupBy(value); }}
            aria-label={intl.formatMessage(messages.weekGroupByAria)}
            sx={getToggleButtonGroupSx(cmmsColors)}
          >
            <ToggleButton value={WeekGroupBy.Department}>
              <FormattedMessage {...messages.weekGroupByDepartment} />
            </ToggleButton>
            <ToggleButton value={WeekGroupBy.Trade}>
              <FormattedMessage {...messages.weekGroupByTrade} />
            </ToggleButton>
          </ToggleButtonGroup>
        }
      </Box>

      {isWeekLoading &&
        <Box sx={{ ...getAssetHierarchyPanelSx(cmmsColors), p: 2 }}>
          <CmmsListSkeleton rows={6} height={36} />
        </Box>
      }

      {/*
        * Above the week, not in place of it: the raised orders are still right. The sums leave
        * out what the schedules will add, and this says so.
        */}
      {!isWeekLoading && showsProjection && projectedQuery.isError &&
        <Box sx={{ ...getAssetHierarchyPanelSx(cmmsColors), mb: 2 }}>
          <CmmsErrorState
            dense
            error={projectedQuery.error}
            fallback={intl.formatMessage(messages.weekProjectedLoadFailed)}
            onRetry={() => projectedQuery.refetch()}
            isRetrying={projectedQuery.isFetching}
          />
        </Box>
      }

      {!isWeekLoading && groups.length === 0 &&
        <Box sx={{ ...getAssetHierarchyPanelSx(cmmsColors), py: 4 }}>
          <CmmsEmptyState
            icon={<EventBusyIcon />}
            title={intl.formatMessage(messages.weekEmptyTitle, { weekNumber: week.weekNumber })}
            hint={intl.formatMessage(messages.weekEmptyHint)}
          />
        </Box>
      }

      {!isWeekLoading && groups.length > 0 &&
        <DndContext {...dndProps}>
          <Box ref={weekRef} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {groups.map((group) => (
              <Box key={group.id ?? "none"} sx={getAssetHierarchyPanelSx(cmmsColors)}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 1,
                    px: 1.5,
                    py: 1.25,
                    borderBottom: `1px solid ${cmmsColors.border}`,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ ...getPanelTitleSx(cmmsColors), ...(group.id === null ? { color: cmmsColors.mutedText, fontStyle: "italic" } : {}) }}
                  >
                    {group.label ?? noGroupLabel}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 1.5 }}>
                    <Typography variant="body2" sx={{ color: cmmsColors.mutedText }}>
                      {intl.formatMessage(messages.weekCounts, { acute: group.acute.length, planned: group.planned.length })}
                    </Typography>
                    {showEstimates &&
                      <EstimateSummary
                        estimatedHours={group.estimatedHours}
                        withoutEstimateCount={group.withoutEstimateCount}
                        palette={cmmsColors}
                      />
                    }
                  </Box>
                </Box>

                <WeekGrid
                  columns={hasLate ? [group.columns.late, ...group.columns.days] : group.columns.days}
                  groupKey={group.id ?? "none"}
                  drag={drag}
                  showEstimates={showEstimates}
                  shades={shades}
                  palette={cmmsColors}
                  cardProps={cardProps}
                />
              </Box>
            ))}
          </Box>
          {/* No animation on a drop: the card is drawn on its new day as soon as the move is saved. */}
          <DragOverlay dropAnimation={null}>
            {drag &&
              <DraggedCard
                row={drag.row}
                acute={drag.acute}
                assetNodeNameById={assetNodeNameById}
                userNameById={userNameById}
                tradeTagById={cardTradeTags}
                palette={cmmsColors}
              />
            }
          </DragOverlay>
        </DndContext>
      }

      <MoveUndoSnackbar notice={notice} onClose={closeNotice} onUndo={handleUndo} />
    </Box>
  );
}
